// src/features/form/FormCreateDialog.tsx
// "+ New form" — the desktop-app create dialog (docs/ux/14-forms.md), built on
// the WIP form-kit (FormDialog/TitleInput/AutoGrowTextarea) from src/components/form.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea } from '@/components/form';
import { useForm } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import { makeEntity, errMessage } from './helpers';

export interface FormCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onError: (message: string) => void;
}

export function FormCreateDialog({ open, onClose, onError }: FormCreateDialogProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const scope = useScope();
  const store = useForm();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dirty = !!(name.trim() || description.trim());

  const close = () => {
    setName('');
    setDescription('');
    onClose();
  };

  const submit = async () => {
    if (!name.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const created = await store.CreateForm({
        entity: makeEntity('', 'form', scope),
        name: name.trim(),
        description: description.trim() || undefined,
      });
      close();
      if (created?.entity?.id) navigate(`/forms/${created.entity.id}`);
    } catch (e) {
      onError(errMessage(e, t('form.builder.createError')));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('form.builder.createSubmit')}
      canSubmit={!!name.trim()}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('form.builder.createTitle')}
    >
      <div className="space-y-2 px-3.5 pb-4 pt-2">
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={t('form.builder.namePlaceholder')}
          initialFocus
          onEnter={() => document.getElementById('form-create-description')?.focus()}
          aria-label={t('form.builder.namePlaceholder')}
        />
        <AutoGrowTextarea
          id="form-create-description"
          value={description}
          onChange={setDescription}
          placeholder={t('form.builder.descriptionPlaceholder')}
          minRows={2}
        />
      </div>
    </FormDialog>
  );
}
